import {
  Badge,
  Box,
  Flex,
  Heading,
  HStack,
  Icon,
  Text,
  useColorMode,
} from "@chakra-ui/react";
import Link from "next/link";
import { FiArrowRight, FiCalendar } from "react-icons/fi";

import type { NewsPostProps } from "./News";

export default function NewsCardSecondary({
  title,
  description,
  date_created,
  media,
  slug,
  category,
}: NewsPostProps) {
  const { colorMode } = useColorMode();
  const date = new Date(date_created);
  const fullURL = `${process.env.NEXT_PUBLIC_DIRECTUS_URL}/assets/${media}?width=600`;

  return (
    <Link href={`/posts/${slug}`} passHref>
      <Box
        bg={colorMode === "light" ? "white" : "neutral.800"}
        borderRadius="xl"
        overflow="hidden"
        h="100%"
        boxShadow={
          colorMode === "light"
            ? "0 10px 40px rgba(0, 0, 0, 0.08)"
            : "0 10px 40px rgba(0, 0, 0, 0.3)"
        }
        border="1px solid"
        borderColor={colorMode === "light" ? "neutral.100" : "neutral.800"}
        transition="opacity 0.3s ease, transform 0.3s ease"
        _hover={{
          transform: "translateY(-6px)",
          boxShadow:
            colorMode === "light"
              ? "0 20px 50px rgba(0, 0, 0, 0.12)"
              : "0 20px 50px rgba(0, 0, 0, 0.4)",
        }}
        cursor="pointer"
        position="relative"
        role="group"
      >
        {/* Image Container */}
        <Box position="relative" overflow="hidden">
          <Box
            h={{ base: "200px", md: "220px" }}
            bgSize="cover"
            bgPosition="center"
            style={{
              backgroundImage: `url(${fullURL})`,
            }}
            transition="transform 0.5s ease"
            _groupHover={{ transform: "scale(1.08)" }}
          />
          {/* Overlay gradient */}
          <Box
            position="absolute"
            bottom={0}
            left={0}
            right={0}
            h="60%"
            bgGradient="linear(to-t, rgba(0,0,0,0.5), transparent)"
          />
          {/* Category badge on image */}
          <Badge
            position="absolute"
            top={4}
            left={4}
            bg="brand.500"
            color="white"
            px={3}
            py={1}
            borderRadius="full"
            fontSize="xs"
            fontWeight={700}
            textTransform="uppercase"
            letterSpacing="0.05em"
            backdropFilter="blur(10px)"
          >
            {category}
          </Badge>
        </Box>

        {/* Content */}
        <Flex direction="column" p={6} h="calc(100% - 220px)" minH="180px">
          <Heading
            fontSize={{ base: "lg", md: "xl" }}
            fontWeight={700}
            color={
              colorMode === "light" ? "text.light-primary" : "text.dark-primary"
            }
            lineHeight="1.4"
            mb={3}
            noOfLines={2}
          >
            {title}
          </Heading>

          <Text
            color={
              colorMode === "light" ? "text.light-secondary" : "text.dark-muted"
            }
            fontSize="sm"
            lineHeight="1.7"
            mb={4}
            noOfLines={2}
            flex={1}
          >
            {description}
          </Text>

          <Flex justify="space-between" align="center" mt="auto">
            <HStack
              spacing={2}
              color={
                colorMode === "light" ? "text.light-muted" : "text.dark-muted"
              }
            >
              <Icon as={FiCalendar} boxSize={3.5} aria-hidden="true" />
              <Text fontSize="xs" fontWeight={500}>
                {new Intl.DateTimeFormat("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                }).format(date)}
              </Text>
            </HStack>

            <HStack
              spacing={1}
              color="brand.500"
              fontWeight={600}
              fontSize="sm"
              opacity={0}
              transform="translateX(-10px)"
              transition="transform 0.3s ease, box-shadow 0.3s ease"
              _groupHover={{ opacity: 1, transform: "translateX(0)" }}
            >
              <Text>Read</Text>
              <Icon as={FiArrowRight} boxSize={4} aria-hidden="true" />
            </HStack>
          </Flex>
        </Flex>
      </Box>
    </Link>
  );
}
